import { useEffect, useCallback, useId, useRef } from 'react';
import { useManualRowOptions } from '@/contexts/ManualRowOptionsContext';
import { X } from 'lucide-react';
import { pushModal, popModal, isTopmostModal } from '@/lib/modalStack';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/** Whether the browser skips ``element`` on Tab: it sits inside a closed details, after its summary. */
function hiddenInClosedDetails(element: HTMLElement): boolean {
  let node: HTMLElement | null = element;
  while (node) {
    const details: HTMLDetailsElement | null = node.parentElement?.closest('details') ?? null;
    if (!details) return false;
    if (!details.open && details.querySelector(':scope > summary') !== node) return true;
    node = details;
  }
  return false;
}

/** The controls a Tab press can reach inside ``panel``, in document order. */
function focusableElements(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => element.getAttribute('aria-hidden') !== 'true' && !hiddenInClosedDetails(element));
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /**
   * Controls rendered on the title row, after the title and before the close
   * button (e.g. a title input and category select), so a full-screen editor
   * does not spend a second row on its metadata fields.
   */
  headerContent?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  widthClass?: string;
  hideCloseButton?: boolean;
  /** Custom content height class. Default: "max-h-[65vh]". Use "max-h-none" to remove limit. */
  contentHeightClass?: string;
  /** Set to false if children handle their own scrolling. Default: true */
  contentOverflow?: boolean;
  /** When true, dialog fills the entire viewport with no margins or rounded corners */
  fullScreen?: boolean;
  /** Fill the viewport on mobile and use a spacious responsive frame on desktop. */
  fillViewport?: boolean;
  /** Reduce header/footer padding for dialogs with persistent toolbars. */
  compactChrome?: boolean;
  /**
   * Optional save/confirm handler. When provided, pressing Cmd+Enter (Mac) or
   * Ctrl+Enter (Windows/Linux) while this dialog is the topmost one triggers it,
   * giving every modal a consistent keyboard shortcut for its primary action.
   */
  onSubmit?: () => void;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  headerContent,
  children,
  footer,
  widthClass = 'max-w-2xl',
  hideCloseButton = false,
  contentHeightClass = 'max-h-[65vh]',
  contentOverflow = true,
  fullScreen = false,
  fillViewport = false,
  compactChrome = false,
  onSubmit,
}: DialogProps) {
  const manualRowOptions = useManualRowOptions();
  if (manualRowOptions && description) {
    description = description.replace(/\([^)]*유형의 모든 문항에 적용됩니다\)/g, '(현재 문항에만 적용됩니다)');
  }
  // Unique ID for this dialog instance
  const dialogId = useRef(Symbol('dialog'));
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  // Register/unregister dialog in the shared modal stack when open state changes
  useEffect(() => {
    if (open) {
      const currentDialogId = dialogId.current;
      pushModal(currentDialogId);
      return () => {
        popModal(currentDialogId);
      };
    }
    return undefined;
  }, [open]);

  // Focus management: on open, focus moves into the dialog (the first
  // control of the content, before the header's close button; the panel
  // itself when nothing can take it) unless the content already placed it
  // (autoFocus); on close or unmount it returns to the element that had it.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      const [first] = focusableElements(panel).filter((element) => element !== closeButtonRef.current);
      (first ?? closeButtonRef.current ?? panel).focus();
    }
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  // Handle ESC (close), Tab (stay inside) and Cmd/Ctrl+Enter (submit) - only for the topmost modal
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Only the topmost modal reacts to keyboard shortcuts
      if (!isTopmostModal(dialogId.current)) return;

      if (event.key === 'Escape') {
        // Prevent parent/global ESC handlers from also firing
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }

      // Tab wraps inside the panel; a focus that has left it comes back in.
      if (event.key === 'Tab' && panelRef.current) {
        const panel = panelRef.current;
        const elements = focusableElements(panel);
        const active = document.activeElement;
        const inside = panel.contains(active);
        if (elements.length === 0) {
          event.preventDefault();
          panel.focus();
        } else if (event.shiftKey && (!inside || active === elements[0])) {
          event.preventDefault();
          elements[elements.length - 1].focus();
        } else if (!event.shiftKey && (!inside || active === elements[elements.length - 1])) {
          event.preventDefault();
          elements[0].focus();
        }
        return;
      }

      // Cmd+Enter (Mac) / Ctrl+Enter (Windows/Linux) triggers the primary action.
      // Ignore while an IME composition is in progress so confirming Korean text
      // does not accidentally submit the form.
      if (
        onSubmit &&
        event.key === 'Enter' &&
        (event.metaKey || event.ctrlKey) &&
        !event.isComposing
      ) {
        event.preventDefault();
        event.stopPropagation();
        onSubmit();
      }
    },
    [onClose, onSubmit]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    return undefined;
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={
          fullScreen
            ? 'relative flex h-full w-full flex-col overflow-hidden bg-white'
            : fillViewport
              ? 'relative flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-xl md:h-[92dvh] md:w-[95vw] md:max-w-[90rem] md:rounded-lg'
              : `relative mx-4 max-h-[90vh] w-full overflow-hidden rounded-lg bg-white shadow-xl ${widthClass}`
        }
      >
        <div className={`flex shrink-0 justify-between gap-4 border-b border-gray-200 px-6 ${compactChrome ? 'items-center py-2' : 'items-start py-4'}`}>
          <div className={headerContent ? 'flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2' : undefined}>
            <div className="shrink-0">
              <h2 id={titleId} className="text-lg font-semibold text-gray-900">{title}</h2>
              {description ? (
                <p className="mt-1 text-sm text-gray-500">{description}</p>
              ) : null}
            </div>
            {headerContent ? <div className="min-w-0 flex-1">{headerContent}</div> : null}
          </div>
          {!hideCloseButton && (
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus-visible:ring focus-visible:ring-amber-500 focus-visible:ring-offset-2"
              aria-label="Close dialog"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <div
          className={
            fullScreen || fillViewport
              ? `min-h-0 flex-1 ${contentOverflow ? 'overflow-y-auto' : 'overflow-hidden'} px-6 py-4`
              : `${contentHeightClass} ${contentOverflow ? 'overflow-y-auto' : 'overflow-hidden'} px-6 py-4`
          }
        >
          {children}
        </div>
        {footer ? (
          <div className={`shrink-0 border-t border-gray-200 px-6 ${compactChrome ? 'py-2' : 'py-4'}`}>{footer}</div>
        ) : null}
      </div>
    </div>
  );
}
